import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  CircularProgress
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { BacktestResult } from '../../services/backtestService';
import { Line, Scatter } from 'react-chartjs-2';
import 'chart.js/auto';

interface BacktestResultsProps {
  result: BacktestResult | null;
  loading: boolean;
  error?: string | null;
}

export const BacktestResults: React.FC<BacktestResultsProps> = ({ result, loading, error }) => {
  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">Running Backtest...</Typography>
          <Typography variant="body2" color="text.secondary">This may take a few moments</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" color="error" gutterBottom>
          Backtest Error
        </Typography>
        <Typography variant="body1">{error}</Typography>
      </Paper>
    );
  }

  if (!result) {
    return (
      <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Run a backtest to see results here
        </Typography>
      </Paper>
    );
  }

  // Format percentage with 2 decimal places
  const formatPercent = (value: number) => {
    return (value * 100).toFixed(2) + '%';
  };

  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Create price chart data and signals data separately
  const priceChartData = {
    labels: result.chart.dates,
    datasets: [
      {
        label: 'Price',
        data: result.chart.prices.map((price, i) => ({ x: result.chart.dates[i], y: price })),
        fill: false,
        borderColor: 'rgba(75, 192, 192, 1)',
        tension: 0.1
      }
    ]
  };
  
  // Create buy signals chart
  const buySignalsData = {
    labels: result.chart.dates,
    datasets: [
      {
        type: 'scatter' as const,
        label: 'Buy Signals',
        data: result.chart.buySignals.map(signal => ({ x: signal.date, y: signal.price })),
        backgroundColor: 'rgba(33, 150, 83, 1)',
        borderColor: 'rgba(33, 150, 83, 1)',
        pointRadius: 6,
        pointHoverRadius: 8
      }
    ]
  };
  
  // Create sell signals chart
  const sellSignalsData = {
    labels: result.chart.dates,
    datasets: [
      {
        type: 'scatter' as const,
        label: 'Sell Signals',
        data: result.chart.sellSignals.map(signal => ({ x: signal.date, y: signal.price })),
        backgroundColor: 'rgba(235, 87, 87, 1)',
        borderColor: 'rgba(235, 87, 87, 1)',
        pointRadius: 6,
        pointHoverRadius: 8
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'month' as const
        },
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Price ($)'
        }
      }
    }
  };

  // Equity curve data
  const equityChartData = {
    labels: result.equity.map(e => e.date),
    datasets: [
      {
        label: 'Portfolio Value',
        data: result.equity.map(e => e.value),
        fill: false,
        borderColor: 'rgba(54, 162, 235, 1)',
        tension: 0.1
      }
    ]
  };

  return (
    <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Backtest Results
      </Typography>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Performance Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Return
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {result.metrics.totalReturn >= 0 ? (
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                ) : (
                  <TrendingDownIcon color="error" sx={{ mr: 1 }} />
                )}
                <Typography variant="h5" color={result.metrics.totalReturn >= 0 ? 'success.main' : 'error.main'}>
                  {formatPercent(result.metrics.totalReturn)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sharpe Ratio
              </Typography>
              <Typography variant="h5">
                {result.metrics.sharpeRatio.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Max Drawdown
              </Typography>
              <Typography variant="h5" color="error.main">
                {formatPercent(result.metrics.maxDrawdown)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Win Rate
              </Typography>
              <Typography variant="h5">
                {formatPercent(result.metrics.winRate)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Profit Factor
              </Typography>
              <Typography variant="h5">
                {result.metrics.profitFactor.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Annualized Return
              </Typography>
              <Typography variant="h5" color={result.metrics.annualizedReturn >= 0 ? 'success.main' : 'error.main'}>
                {formatPercent(result.metrics.annualizedReturn)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Strategy Performance</Typography>
          <Box sx={{ height: 400 }}>
            <Line data={equityChartData} options={chartOptions} />
          </Box>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Price Chart with Signals</Typography>
          <Box sx={{ height: 400 }}>
            <Line data={priceChartData} options={chartOptions} />
          </Box>
          {result.chart.buySignals.length > 0 && (
            <Box sx={{ height: 50, mt: 1 }}>
              <Scatter data={buySignalsData} options={{
                ...chartOptions,
                scales: {
                  ...chartOptions.scales,
                  y: { display: false }
                }
              }} />
            </Box>
          )}
          {result.chart.sellSignals.length > 0 && (
            <Box sx={{ height: 50, mt: 1 }}>
              <Scatter data={sellSignalsData} options={{
                ...chartOptions,
                scales: {
                  ...chartOptions.scales,
                  y: { display: false }
                }
              }} />
            </Box>
          )}
        </Grid>
      </Grid>
      
      {/* Trades Table */}
      <Typography variant="h6" gutterBottom>Trade History</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.trades.map((trade, index) => (
              <TableRow key={index}>
                <TableCell>{new Date(trade.date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip 
                    label={trade.type} 
                    size="small"
                    color={trade.type.toLowerCase() === 'buy' ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell align="right">{formatCurrency(trade.price)}</TableCell>
                <TableCell align="right">{trade.qty}</TableCell>
                <TableCell align="right">{formatCurrency(trade.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default BacktestResults;
